(() => {
  const messages = JSON.parse(document.querySelector('#StorefrontStrings')?.textContent || '{}');
  const message = (key, fallback, values = {}) => Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), messages[key] || fallback);
  const dispatchEligible = (settings, inventory, now = new Date()) => {
    if (inventory.available !== 'true' || !inventory.management || Number(inventory.quantity) <= 0) return false;
    const cutoff = Number(settings.cutoff);
    const weekdays = settings.weekdays.split(',').filter(Boolean).map(Number);
    if (!Number.isInteger(cutoff) || cutoff < 1 || cutoff > 23 || !weekdays.length) return false;
    try {
      const parts = new Intl.DateTimeFormat('en-GB', { timeZone: settings.timezone, weekday: 'short', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' }).formatToParts(now);
      const part = (type) => parts.find(value => value.type === type)?.value;
      const day = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[part('weekday')];
      const date = `${part('year')}-${part('month')}-${part('day')}`;
      const blackout = (settings.blackout || '').split(/[\s,;]+/).filter(Boolean);
      return weekdays.includes(day) && Number(part('hour')) < cutoff && !blackout.includes(date);
    } catch { return false; }
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-auto-submit]').forEach((select) => {
      if (select.dataset.enhanced) return;
      select.dataset.enhanced = 'true';
      select.form.classList.add('is-enhanced');
      select.addEventListener('change', () => select.form.requestSubmit());
    });

    scope.querySelectorAll('[data-product-section]').forEach((section) => {
      if (section.dataset.enhanced) return;
      section.dataset.enhanced = 'true';
      const gallery = section.querySelector('[data-product-gallery]');
      const frames = [...section.querySelectorAll('[data-media-id][data-media-type]')];
      const photos = frames.filter(frame => frame.dataset.mediaType === 'image');
      const thumbnails = [...section.querySelectorAll('[data-product-thumbnail]')];
      const galleryCounter = section.querySelector('[data-gallery-counter]');
      const zoom = section.querySelector('[data-product-zoom]');
      const zoomImage = section.querySelector('[data-zoom-image]');
      const zoomCounter = section.querySelector('[data-zoom-counter]');
      let activeIndex = Math.max(0, frames.findIndex(frame => frame.dataset.active === 'true'));
      let zoomReturnFocus;
      const stopMedia = (frame) => {
        frame.querySelectorAll('video').forEach(video => video.pause());
        frame.querySelectorAll('iframe').forEach(iframe => {
          try {
            const origin = new URL(iframe.src).origin;
            if (origin.includes('youtube.com') || origin.includes('youtube-nocookie.com')) iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), origin);
            if (origin.includes('vimeo.com')) iframe.contentWindow?.postMessage({ method: 'pause' }, origin);
          } catch { /* A missing media URL has nothing to pause. */ }
        });
      };
      const updateZoom = () => {
        const frame = frames[activeIndex];
        if (!frame || frame.dataset.mediaType !== 'image' || !zoomImage) return;
        zoomImage.src = frame.dataset.zoomSource;
        zoomImage.alt = frame.dataset.alt || '';
        if (zoomCounter) zoomCounter.textContent = `${photos.indexOf(frame) + 1} / ${photos.length}`;
      };
      const selectMedia = (index) => {
        if (!frames.length) return;
        const previousIndex = activeIndex;
        activeIndex = (index + frames.length) % frames.length;
        frames.forEach((frame, frameIndex) => {
          frame.hidden = frameIndex !== activeIndex;
          if (frame.hidden) stopMedia(frame);
        });
        thumbnails.forEach(thumbnail => {
          const selected = thumbnail.dataset.productThumbnail === frames[activeIndex].dataset.mediaId;
          thumbnail.classList.toggle('is-active', selected);
          if (selected) thumbnail.setAttribute('aria-current', 'true');
          else thumbnail.removeAttribute('aria-current');
        });
        if (activeIndex !== previousIndex && document.body.dataset.motion !== 'false' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          const incoming = frames[activeIndex];
          incoming.getAnimations?.().forEach(animation => animation.cancel());
          incoming.animate?.([{ opacity: .45, transform: `translateX(${index > previousIndex ? 8 : -8}px)` }, { opacity: 1, transform: 'translateX(0)' }], { duration: 240, easing: 'cubic-bezier(.16,1,.3,1)' });
        }
        if (galleryCounter) galleryCounter.textContent = `${activeIndex + 1} / ${frames.length}`;
        if (zoom?.open) updateZoom();
      };
      if (gallery) gallery.dataset.enhanced = 'true';
      selectMedia(activeIndex);
      section.querySelector('[data-gallery-controls]')?.removeAttribute('hidden');
      section.querySelector('[data-gallery-previous]')?.addEventListener('click', () => selectMedia(activeIndex - 1));
      section.querySelector('[data-gallery-next]')?.addEventListener('click', () => selectMedia(activeIndex + 1));
      thumbnails.forEach(thumbnail => thumbnail.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const index = frames.findIndex(frame => frame.dataset.mediaId === thumbnail.dataset.productThumbnail);
        if (index >= 0) selectMedia(index);
      }));
      const moveZoom = (direction) => {
        if (!photos.length) return;
        const next = (photos.indexOf(frames[activeIndex]) + direction + photos.length) % photos.length;
        selectMedia(frames.indexOf(photos[next]));
      };
      if (zoom && typeof zoom.showModal === 'function') {
        section.querySelectorAll('[data-zoom-open]').forEach(trigger => trigger.addEventListener('click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          const index = frames.indexOf(trigger.closest('[data-media-id]'));
          if (index >= 0) selectMedia(index);
          zoomReturnFocus = trigger;
          updateZoom();
          zoom.showModal();
          document.documentElement.classList.add('product-zoom-is-open');
        }));
        section.querySelector('[data-zoom-close]').addEventListener('click', () => zoom.close());
        section.querySelector('[data-zoom-previous]')?.addEventListener('click', () => moveZoom(-1));
        section.querySelector('[data-zoom-next]')?.addEventListener('click', () => moveZoom(1));
        zoom.addEventListener('keydown', event => {
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); moveZoom(event.key === 'ArrowLeft' ? -1 : 1); }
        });
        zoom.addEventListener('click', event => { if (event.target === zoom) zoom.close(); });
        zoom.addEventListener('close', () => {
          document.documentElement.classList.remove('product-zoom-is-open');
          const activeLink = frames[activeIndex]?.querySelector('[data-zoom-open]');
          (activeLink || zoomReturnFocus)?.focus({ preventScroll: true });
        });
      }

      const inventory = section.querySelector('[data-product-stock]');
      const dispatch = section.querySelector('[data-dispatch]');
      const refreshDispatch = () => { if (dispatch) dispatch.hidden = !inventory || !dispatchEligible(dispatch.dataset, inventory.dataset); };
      const updateInventory = (data) => {
        if (!inventory) return;
        ['available', 'management', 'quantity', 'policy'].forEach(key => { inventory.dataset[key] = data[key] || ''; });
        const available = data.available === 'true';
        const count = Number(data.quantity);
        let label = message('stock_available', 'Beschikbaar om te bestellen.');
        let state = 'available';
        if (!available) { label = message('stock_sold_out', 'Op dit moment uitverkocht.'); state = 'sold-out'; }
        else if (data.management && count > 0) {
          state = 'in-stock';
          label = section.dataset.showStockCount === 'true' && count <= Number(section.dataset.stockThreshold) ? message('stock_low', 'Nog [count] beschikbaar.', { count }) : message('stock_in', 'Op voorraad.');
        } else if (data.management && data.policy === 'continue') { label = message('stock_backorder', 'Beschikbaar op nabestelling.'); state = 'backorder'; }
        inventory.querySelector('[data-stock-label]').textContent = label;
        inventory.dataset.state = state;
        refreshDispatch();
      };
      if (inventory) updateInventory({ ...inventory.dataset });
      let dispatchTimer;
      if (dispatch) dispatchTimer = setInterval(() => {
        if (!section.isConnected) { clearInterval(dispatchTimer); return; }
        refreshDispatch();
      }, 30000);
      const enhancedVariant = section.querySelector('[data-enhanced-variant]');
      if (enhancedVariant) enhancedVariant.hidden = false;
      const variantSelect = section.querySelector('[data-variant-select]');
      const choices = section.querySelector('[data-variant-choices]');
      if (choices && variantSelect) {
        enhancedVariant.hidden = true;
        choices.hidden = false;
        choices.addEventListener('change', event => {
          if (!event.target.matches('input[type="radio"]')) return;
          variantSelect.value = event.target.value;
          variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      variantSelect?.addEventListener('change', () => {
        const variant = variantSelect.selectedOptions[0];
        if (!variant) return;
        choices?.querySelectorAll('input').forEach(input => { input.checked = input.value === variant.value; });
        const available = variant.dataset.available === 'true';
        section.querySelector('[data-variant-id]').value = variant.value;
        section.querySelectorAll('[data-product-price]').forEach(price => { price.textContent = variant.dataset.price; });
        const compare = section.querySelector('[data-product-compare]');
        if (compare) compare.hidden = !variant.dataset.compare;
        const compareValue = section.querySelector('[data-product-compare-value]');
        if (compareValue) compareValue.textContent = variant.dataset.compare || '';
        section.querySelectorAll('[data-add-to-cart]').forEach(button => { button.disabled = !available; });
        section.querySelectorAll('[data-add-to-cart-label]').forEach(label => { label.textContent = available ? section.dataset.addLabel : message('sold_out', 'Uitverkocht'); });
        const quantity = section.querySelector('[name="quantity"]');
        quantity.min = variant.dataset.min || '1';
        quantity.step = variant.dataset.increment || '1';
        if (variant.dataset.max) quantity.max = variant.dataset.max;
        else quantity.removeAttribute('max');
        quantity.value = quantity.min;
        const imageIndex = frames.findIndex(frame => (variant.dataset.mediaId && frame.dataset.mediaId === variant.dataset.mediaId) || (variant.dataset.image && frame.dataset.source === variant.dataset.image));
        if (imageIndex >= 0) selectMedia(imageIndex);
        updateInventory(variant.dataset);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.value);
        window.history.replaceState({}, '', url);
      });

      const sticky = section.querySelector('[data-product-sticky]');
      const purchaseButton = section.querySelector('.product-form [data-add-to-cart]');
      let purchaseObserver;
      if (sticky && purchaseButton && 'IntersectionObserver' in window) {
        purchaseObserver = new IntersectionObserver(entries => {
          const entry = entries[0];
          sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
        }, { threshold: 0 });
        purchaseObserver.observe(purchaseButton);
      }
      section.addEventListener('cozy:cleanup', () => {
        clearInterval(dispatchTimer);
        purchaseObserver?.disconnect();
        if (zoom?.open) zoom.close();
      }, { once: true });
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-product-section]').forEach(section => section.dispatchEvent(new Event('cozy:cleanup'))));
})();
